import * as _ from 'lodash';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../shared/Locator';
import { ButtonGroup, DocsLink, icons } from '../../generic';
import Button from '../../generic/Button';
import Tabs from '../../generic/Tabs';
import Text from '../../generic/Text';
import type { ManagePackageLabelsState } from '../../projects/dependenciesV2/hooks/useManagePackageLabelsModal';

import TabContent from './TabContent';
import { Tab } from './types';
import { usePackageLabelState } from './usePackageLabelState';

import styles from './ModalManagePackageLabels.module.less';

/**
/**
 * Message shown on other, disabled tabs when the current tab has pending changes.
 */
const PENDING_CHANGES_TOOLTIP = 'Please save or cancel any pending label changes.';

type ModalProps = {
  context: ManagePackageLabelsState;
  // The locator of the user revision that imports the dependency.
  // If provided, then an issue scan will be performed after any changes are made.
  parentRevisionLocator?: string;

  // Modal state management
  show: boolean;
  onHide(): void;

  // Enabled Tabs
  isGlobalEnabled?: boolean;
  isProjectEnabled?: boolean;
  isRevisionEnabled?: boolean;
};

const TabIcons = {
  [Tab.Revision]: icons.codeCommit,
  [Tab.Project]: icons.folderBookmark,
  [Tab.Global]: icons.globeOutline,
} as const;

const ModalManagePackageLabels: VoidFunctionComponent<ModalProps> = ({
  onHide,
  show,
  context: { revision, title },
  parentRevisionLocator,
  isGlobalEnabled,
  isProjectEnabled,
  isRevisionEnabled,
}) => {
  const packageId = Locator.parsePackageString(revision || '');
  const parentProjectLocator = Locator.parsePackageString(parentRevisionLocator || '');
  const state = usePackageLabelState(packageId);

  // Keep track of which tabs are "dirty" meaning that their current state
  // differs from their initial state.
  const dirtyTabs: Record<Tab, boolean> = {
    [Tab.Global]: !_.isEqual(state.globalLabelsSelected, state.getInitialGlobalAssignments()),
    [Tab.Project]: !_.isEqual(state.projectLabelsSelected, state.getInitialProjectAssignments(parentProjectLocator)),
    [Tab.Revision]: !_.isEqual(state.revisionLabelsSelected, state.getInitialRevisionAssignments(parentRevisionLocator ?? '')),
  };
  const isAnyTabDirty = Object.values(dirtyTabs).some((isDirty) => isDirty);

  if (
    !show ||
    !revision ||
    (isRevisionEnabled && !parentRevisionLocator) ||
    (!isGlobalEnabled && !isProjectEnabled && !isRevisionEnabled)
  ) {
    return null;
  }

  const onSave = () => {
    switch (state.tabs.activeTab) {
      case Tab.Global:
        if (!isGlobalEnabled) {
          return () => {};
        }
        return state.onSaveGlobal();
      case Tab.Project:
        if (!isProjectEnabled) {
          return () => {};
        }
        return state.onSaveProject(parentProjectLocator);
      case Tab.Revision:
        if (!isRevisionEnabled || !parentRevisionLocator) {
          return () => {};
        }
        return state.onSaveRevision(parentRevisionLocator);
      default:
        return () => {};
    }
  };

  return (
    <Modal
      onHide={onHide}
      animation
      show={show}
      backdrop="static"
      keyboard={false}
      className={styles.modalManagePackageLabels}
    >
      <Modal.Header closeButton>
        <Modal.Title>Manage Labels</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <span className={styles.helpText}>
          You can create and manage labels in{' '}
          <Link className={styles.link} to="/account/settings/organization">
            Organization Settings
          </Link>
          . Learn more about using labels in our <DocsLink to="using-package-labels">docs</DocsLink>.
        </span>
        <Text.H3>{title}</Text.H3>
        <Tabs>
          {state.tabs.tabs.map((tab) => {
            if (!isGlobalEnabled && tab.label === Tab.Global) {
              return null;
            }
            if (!isProjectEnabled && tab.label === Tab.Project) {
              return null;
            }
            if (!isRevisionEnabled && tab.label === Tab.Revision) {
              return null;
            }

            // If a different tab is dirty, disable this one (with a tooltip).
            const isThisTabDirty = dirtyTabs[tab.label];
            const isDisabled = isAnyTabDirty && !isThisTabDirty;
            const tooltip = isDisabled ? PENDING_CHANGES_TOOLTIP : undefined;

            return (
              <Tabs.Tab
                key={tab.label}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...tab}
                icon={TabIcons[tab.label]}
                isDisabled={isDisabled}
                tooltip={tooltip}
              />
            );
          })}
        </Tabs>
        {isGlobalEnabled && (
          <TabContent
            tab={Tab.Global}
            activeTab={state.tabs.activeTab}
            helpText="These labels are applied globally, across all of your projects."
            parentRevisionLocator={parentRevisionLocator}
            state={state}
            labelsSelected={state.globalLabelsSelected}
            setLabelsSelected={state.setGlobalLabelsSelected}
            hasNoPermission={state.hasNoPermission}
          />
        )}
        {isProjectEnabled && (
          <TabContent
            tab={Tab.Project}
            activeTab={state.tabs.activeTab}
            helpText="These labels are applied only in this project."
            parentRevisionLocator={parentRevisionLocator}
            state={state}
            labelsSelected={state.projectLabelsSelected}
            setLabelsSelected={state.setProjectLabelsSelected}
            hasNoPermission={state.hasNoPermission}
          />
        )}
        {isRevisionEnabled && (
          <TabContent
            tab={Tab.Revision}
            activeTab={state.tabs.activeTab}
            helpText="These labels are only visible in the current revision of this project."
            parentRevisionLocator={parentRevisionLocator}
            state={state}
            labelsSelected={state.revisionLabelsSelected}
            setLabelsSelected={state.setRevisionLabelsSelected}
            hasNoPermission={state.hasNoPermission}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={onHide}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            isDisabled={state.hasNoPermission}
            onClick={onSave}
          >
            Save
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalManagePackageLabels;
